import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase';
import { doc, onSnapshot, collection, query, where } from 'firebase/firestore';
import { useParams } from 'react-router-dom';
import { Shield, Globe, Image as ImageIcon, Video, X, PlayCircle, Play, Pause, SkipBack, SkipForward } from 'lucide-react';
import SideNav from '../components/SideNav';

const ProfileView = () => {
  const { uid } = useParams();
  const [userData, setUserData] = useState(null);
  const [publicPhotos, setPublicPhotos] = useState([]);
  const [publicVideos, setPublicVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [galleryTab, setGalleryTab] = useState('PHOTOS');

  const videoRef = useRef(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!uid) return;
    const userRef = doc(db, 'users', uid);
    const unsubUser = onSnapshot(userRef, (snap) => setUserData(snap.data()));

    const mediaQuery = query(collection(db, 'users', uid, 'media'), where('privacy', '==', 'PUBLIC'));
    const unsubMedia = onSnapshot(mediaQuery, (snap) => {
      const docs = snap.docs.map(d => ({id: d.id, ...d.data()}));
      setPublicPhotos(docs.filter(i => i.type === 'IMAGE'));
      setPublicVideos(docs.filter(i => i.type === 'VIDEO'));
      setLoading(false);
    });

    return () => { unsubUser(); unsubMedia(); };
  }, [uid]);

  const openPlayer = (video) => { setSelectedVideo(video); setIsPlaying(true); };
  const closePlayer = () => { setSelectedVideo(null); setIsPlaying(false); };
  const togglePlay = () => { videoRef.current.paused ? videoRef.current.play() : videoRef.current.pause(); setIsPlaying(!videoRef.current.paused); };
  const navigateVideo = (dir) => {
    const idx = publicVideos.findIndex(v => v.id === selectedVideo.id);
    if (publicVideos[idx + dir]) setSelectedVideo(publicVideos[idx + dir]);
  };

  if (loading) return <div style={styles.loader}>ACCESSING PUBLIC DOSSIER...</div>;

  return (
    <div style={styles.layout}>
      <SideNav />
      <div style={styles.main}>
        
        <div style={styles.topSection}>
            <div style={styles.headerLeft}>
                <div style={styles.picWrapper}>
                    <img src={userData?.profilePic || 'https://placehold.co/150'} style={styles.pic} alt="Profile" />
                </div>
                <div style={styles.infoBlock}>
                    <h1 style={styles.name}>{userData?.firstName} {userData?.lastName}</h1>
                    <p style={styles.username}>@{userData?.username}</p>
                    <div style={styles.badge}><Shield size={12}/> {userData?.tier || 'DREAMER'}</div>
                </div>
            </div>
            {!isMobile && (
                <div style={styles.rightSidebar}>
                    <h3 style={styles.sidebarTitle}>COMM LINK</h3>
                    <p style={styles.sidebarText}>STATUS: OFFLINE</p>
                </div>
            )}
        </div>

        <div style={styles.midSection}>
            <button style={styles.galleryBtn} onClick={() => setIsGalleryOpen(true)}>
                <Globe size={16} style={{marginRight:8}}/> OPEN GALLERY
            </button>
            <div style={styles.stylishLine}></div>
        </div>

        <div style={styles.featuredSection}>
            {[0, 1, 2].map(i => (
                <div key={i} style={isMobile ? styles.featuredRowMobile : styles.featuredRow}>
                    <div style={styles.featuredPhotoBox}>
                        {userData?.featured?.[i] ? (
                            <img src={userData.featured[i]} style={styles.featuredImg} alt={'Featured '+i} />
                        ) : (
                            <div style={styles.emptyFeature}><ImageIcon size={40} color="#333"/><span>SLOT {i+1} EMPTY</span></div>
                        )}
                    </div>
                    <div style={styles.featuredTextBox}>
                        {/* DYNAMIC TITLE FIXED HERE */}
                        <h3 style={styles.featureTitle}>
                            {userData?.featuredTitles?.[i] ? userData.featuredTitles[i].toUpperCase() : "FEATURED ENTRY 00" + (i+1)}
                        </h3>
                        <p style={styles.featureText}>
                            {userData?.featuredAbout?.[i] || "Data redacted awaiting user input."}
                        </p>
                    </div>
                </div>
            ))}
        </div>

        {isGalleryOpen && (
            <div style={styles.modalOverlay}>
                <div style={styles.galleryWindow}>
                    <div style={styles.galleryHeader}>
                        <h2 style={styles.galleryTitle}>PUBLIC DOSSIER // GALLERY</h2>
                        <button onClick={() => setIsGalleryOpen(false)} style={styles.closeIconBtn}><X /></button>
                    </div>
                    <div style={styles.galleryTabs}>
                        <button style={galleryTab === 'PHOTOS' ? styles.activeTab : styles.tab} onClick={()=>setGalleryTab('PHOTOS')}>PHOTOS</button>
                        <button style={galleryTab === 'CONTENT' ? styles.activeTab : styles.tab} onClick={()=>setGalleryTab('CONTENT')}>CONTENT</button>
                    </div>
                    <div style={styles.galleryGrid}>
                        {galleryTab === 'PHOTOS' ? (
                            publicPhotos.length === 0 ? <div style={styles.empty}>NO PUBLIC PHOTOS</div> : 
                            publicPhotos.map(item => <img key={item.id} src={item.url} style={styles.gridThumb} />)
                        ) : (
                            publicVideos.length === 0 ? <div style={styles.empty}>NO PUBLIC CONTENT</div> :
                            publicVideos.map(item => (
                                <div key={item.id} style={styles.vidGridThumb} onClick={() => openPlayer(item)}>
                                    <PlayCircle size={40} color="#D4AF37" />
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        )}

        {selectedVideo && (
            <div style={styles.playerOverlay}>
                <div style={styles.playerWindow}>
                    <button onClick={closePlayer} style={styles.closeBtn}><X /></button>
                    <video ref={videoRef} src={selectedVideo.url} autoPlay onTimeUpdate={() => setCurrentTime(videoRef.current.currentTime)} onLoadedMetadata={() => setDuration(videoRef.current.duration)} style={{width: '100%'}} />
                    <div style={styles.playerControls}>
                        <input type="range" min="0" max={duration} value={currentTime} onChange={(e) => {videoRef.current.currentTime = e.target.value; setCurrentTime(e.target.value);}} style={{width: '100%', accentColor: '#D4AF37'}} />
                        <div style={styles.btnRow}>
                            <button onClick={() => navigateVideo(1)} style={styles.navBtn}><SkipBack /></button>
                            <button onClick={togglePlay} style={styles.playBtn}>{isPlaying ? <Pause /> : <Play />}</button>
                            <button onClick={() => navigateVideo(-1)} style={styles.navBtn}><SkipForward /></button>
                        </div>
                    </div>
                </div>
            </div>
        )}

      </div>
    </div>
  );
};

const styles = {
  layout: { display: 'flex', background: '#000', minHeight: '100vh', color: '#fff', fontFamily: 'Inter' },
  loader: { height: '100vh', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#D4AF37', fontFamily: 'Orbitron' },
  main: { flex: 1, padding: '40px', position: 'relative' },
  
  topSection: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '30px' },
  picWrapper: { display: 'flex', justifyContent: 'center' },
  pic: { width: '150px', height: '150px', borderRadius: '50%', border: '3px solid #D4AF37', objectFit: 'cover', boxShadow: '0 0 20px rgba(212,175,55,0.3)' },
  infoBlock: { display: 'flex', flexDirection: 'column' },
  name: { fontFamily: 'Orbitron', fontSize: '2rem', margin: 0, color: '#D4AF37' },
  username: { color: '#888', fontSize: '1.1rem', margin: '5px 0' },
  badge: { border: '1px solid #D4AF37', color: '#D4AF37', padding: '4px 12px', fontSize: '0.8rem', width: 'fit-content', display: 'flex', alignItems: 'center', gap: '5px' },
  
  rightSidebar: { width: '250px', background: '#111', border: '1px solid #333', padding: '20px', height: '200px', marginTop: '20px' },
  sidebarTitle: { fontFamily: 'Orbitron', color: '#666', margin: '0 0 10px 0', fontSize: '1rem' },
  sidebarText: { fontFamily: 'Courier New', color: '#D4AF37' },

  midSection: { marginBottom: '40px' },
  galleryBtn: { background: '#D4AF37', color: '#000', border: 'none', padding: '12px 30px', fontFamily: 'Orbitron', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', marginBottom: '20px' },
  stylishLine: { height: '2px', background: 'linear-gradient(90deg, #D4AF37 0%, transparent 100%)', boxShadow: '0 2px 10px rgba(212,175,55,0.5)' },

  featuredSection: { display: 'flex', flexDirection: 'column', gap: '40px' },
  featuredRow: { display: 'flex', gap: '30px', alignItems: 'center', borderBottom: '1px solid #222', paddingBottom: '30px' },
  featuredRowMobile: { display: 'flex', flexDirection: 'column', gap: '20px', borderBottom: '1px solid #222', paddingBottom: '30px' },
  featuredPhotoBox: { width: '350px', height: '250px', background: '#0a0a0a', border: '1px solid #333', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  featuredImg: { width: '100%', height: '100%', objectFit: 'cover' },
  emptyFeature: { color: '#333', fontFamily: 'Courier New', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  featuredTextBox: { flex: 1 },
  featureTitle: { fontFamily: 'Orbitron', color: '#D4AF37', margin: '0 0 10px 0' },
  featureText: { color: '#888', lineHeight: '1.6' },

  modalOverlay: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.9)', zIndex: 5000, display: 'flex', justifyContent: 'center', alignItems: 'center' },
  galleryWindow: { width: '90%', maxWidth: '900px', background: '#000', border: '2px solid #D4AF37', height: '80vh', display: 'flex', flexDirection: 'column' },
  galleryHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', borderBottom: '1px solid #333', background: '#111' },
  galleryTitle: { fontFamily: 'Orbitron', margin: 0, color: '#D4AF37' },
  closeIconBtn: { background: 'none', border: 'none', color: '#fff', cursor: 'pointer' },
  galleryTabs: { display: 'flex', borderBottom: '1px solid #333' },
  tab: { flex: 1, padding: '15px', background: '#000', color: '#666', border: 'none', fontFamily: 'Orbitron', cursor: 'pointer' },
  activeTab: { flex: 1, padding: '15px', background: '#111', color: '#D4AF37', borderBottom: '2px solid #D4AF37', fontFamily: 'Orbitron' },
  galleryGrid: { padding: '20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '15px', overflowY: 'auto', flex: 1 },
  gridThumb: { width: '100%', height: '150px', objectFit: 'cover', border: '1px solid #333' },
  vidGridThumb: { width: '100%', height: '150px', background: '#111', border: '1px solid #333', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  empty: { gridColumn: '1 / -1', textAlign: 'center', color: '#444', padding: '50px', fontFamily: 'Courier New' },

  playerOverlay: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 },
  playerWindow: { background: '#000', border: '1px solid #333', width: '90%', maxWidth: '800px', position: 'relative', padding: '10px' },
  closeBtn: { position: 'absolute', top: '-40px', right: 0, background: 'none', border: 'none', color: '#fff', cursor: 'pointer' },
  playerControls: { padding: '20px' },
  btnRow: { display: 'flex', justifyContent: 'center', gap: '30px', marginTop: '10px' },
  playBtn: { background: '#D4AF37', border: 'none', width: '50px', height: '50px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  navBtn: { background: 'none', border: 'none', color: '#D4AF37', cursor: 'pointer' }
};

export default ProfileView;
